import * as React from 'react';
import { Head } from 'head/Head';
import { MobileNativeContext } from 'providers/withApplicationContext';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { WidgetType, Widgets } from '@tock/shared/types/widget';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer } from '@tock/coretypes';
import {
  getDocumentAttributesFromTemplate,
  getWidgetTypes,
  mapContentToItems,
} from 'utils/widgetUtils';
import { imageUrl } from '@tock/images';
import { oc } from 'ts-optchain';
import { useSelector } from 'react-redux';
import LoadingComponent from '@tock/shared/components/LoadingComponent';
import WidgetCreatorContent, { WidgetSpecificParams } from 'components/www/WidgetCreatorContent';
import _ from 'lodash';
import loadable from '@loadable/component';

const BasicCarouselWidget = loadable(() => import('consumer-page-widgets/BasicCarouselWidget'), {
  resolveComponent: (components) => components.BasicCarouselWidget,
});

const BasicCarouselWidgetFork = loadable(
  () => import('consumer-page-widgets/ContentTypeAwareCarouselWidget')
);
const GridWidgetLegacy = loadable(() => import('consumer-page-widgets/GridWidgetLegacy'), {
  resolveComponent: (components) => components.GridWidgetLegacy,
});

const MarketingFeaturesWidget = loadable(
  () => import('consumer-page-widgets/MarketingFeaturesWidget')
);
const MarketingWidget = loadable(() => import('consumer-page-widgets/MarketingWidget'));

const CallToActionWidget = loadable(() => import('consumer-page-widgets/CallToActionWidget'), {
  resolveComponent: (components) => components.CallToActionWidget,
});
const ModalTextWidget = loadable(() => import('consumer-page-widgets/ModalTextWidget'));
const StaticHeroWidget = loadable(() => import('consumer-page-widgets/StaticHeroWidget'));
const TitleWidget = loadable(() => import('consumer-page-widgets/TitleWidget'));
const HeroWidget = loadable(() => import('consumer-page-widgets/HeroWidget'));
const DividerWidget = loadable(() => import('consumer-page-widgets/DividerWidget'));
const CollectionPageWidget = loadable(() => import('consumer-page-widgets/CollectionPageWidget'));
const IncludedContentWidget = loadable(() => import('consumer-page-widgets/IncludedContentWidget'));

BasicCarouselWidget.preload();
GridWidgetLegacy.preload();
MarketingFeaturesWidget.preload();
MarketingWidget.preload();

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridContainer: {
      marginTop: 0,
      marginBottom: theme.spacing(4.5),
      [theme.breakpoints.up('md')]: {
        marginBottom: theme.spacing(9),
      },
      '& .MuiGrid-container': {
        marginTop: 0,
      },
    },
    gridContainerFirst: {
      '&:first-child': {
        marginTop: theme.spacing(4.5),
      },
    },
  })
);

interface WidgetConsumerPageContentProps {
  /**
   * standAlone if true means that the first widget section with a title will be the h1 of the page
   */
  standAlone: boolean;
  /**
   * pathname - content widgets key off of urls, so we need the pathname to properly render
   */
  pathname: string;
  /**
   * holds props / params relevant to specific widget types only. If a widget of a relevant type is included on a page,
   * it will pick up said param
   */
  widgetSpecificParams?: WidgetSpecificParams;
}

export const WidgetConsumerPageContent: React.FC<WidgetConsumerPageContentProps> = ({
  standAlone,
  pathname,
  widgetSpecificParams,
}) => {
  const {
    actions: {
      consumerPage: { fetchMoreWidgetData, fetchMoreWidgetDataV2 },
    },
  } = useActions();

  const classes = useStyles();

  const inMobileNative = React.useContext(MobileNativeContext).mobileNative;

  const { consumerPageState } = useSelector((state: StoreType) => {
    return {
      consumerPageState: state.consumerPage,
      app: state.app,
    };
  });

  const { consumerPage, requestInProgress } = consumerPageState;

  const getFirstPageImage = (orderedWidgets: WidgetType[]): string | undefined => {
    const firstWidget = orderedWidgets[0];
    if (!firstWidget || !firstWidget.content) {
      return undefined;
    }

    const widgetData = mapContentToItems(firstWidget.content as consumer.WidgetContent);
    if (widgetData.length === 0 || !widgetData[0].imgSrc) {
      return undefined;
    }
    return imageUrl(widgetData[0].imgSrc, { width: 200, height: 200 });
  };

  const renderWidget = (widgetType: WidgetType, index: number, widgets: WidgetType[]) => {
    const widgetInfo = widgetType.widgetInfo as consumer.WidgetInfo;

    const content = widgetType.content as consumer.WidgetContent;

    if (!widgetInfo) {
      return null;
    }
    if (widgetType.renderType !== Widgets.WidgetCreatorWidgetType && !content) {
      return null;
    }

    const isTitleWidget = index === 0 && standAlone;

    // lazy load the last widget on the page if there are more than 3 and it's a grid widget
    // setting to just Grid widget for now, but may implement this for anything past 3 and any type
    const isLazyLoadable =
      widgets.length > 3 &&
      index > 1 &&
      (widgetType.renderType === Widgets.Grid || widgetType.renderType === Widgets.BasicCarousel);

    const firstChildContainerClassName = `${classes.gridContainer} ${classes.gridContainerFirst}`;

    switch (widgetType.renderType) {
      case Widgets.WidgetCreatorWidgetType: {
        return (
          <WidgetCreatorContent
            key={index}
            widgetSpecificParams={widgetSpecificParams}
            widget={widgetType}
            pageWidgetListIndex={index}
            // this will be able to go away once we migrate all widgets to the new system
            options={{
              fetchMoreWidgetData: fetchMoreWidgetDataV2,
              lazyLoad: isLazyLoadable,
              type: 'EXPLORETOCK',
              slug: pathname,
              pathname,
              className: firstChildContainerClassName,
              isTitle: isTitleWidget,
            }}
          />
        );
      }
      case Widgets.Title: {
        return <TitleWidget isTitle={isTitleWidget} key={index} widget={widgetType} />;
      }
      case Widgets.Hero: {
        return (
          <HeroWidget
            baseWidgetClassName={classes.gridContainer}
            key={index}
            isMobileNative={inMobileNative}
            widget={widgetType}
            pathname={pathname}
          />
        );
      }
      case Widgets.StaticHero: {
        return (
          <StaticHeroWidget className={classes.gridContainer} key={index} widget={widgetType} />
        );
      }
      case Widgets.BasicCarousel: {
        return (
          <BasicCarouselWidgetFork
            className={firstChildContainerClassName}
            id={widgetInfo.id}
            isTitle={isTitleWidget}
            key={index}
            lazyLoad={isLazyLoadable}
            pathname={pathname}
            widget={{ mode: 'legacy', carousel: widgetType }}
            listIndex={index}
          />
        );
      }
      case Widgets.Grid: {
        return (
          <GridWidgetLegacy
            key={index}
            lazyLoad={isLazyLoadable}
            fetchMoreWidgetData={fetchMoreWidgetData}
            type="EXPLORETOCK"
            slug={pathname}
            widget={widgetType}
            pathname={pathname}
            className={firstChildContainerClassName}
            isTitle={isTitleWidget}
            widgetListIndex={index}
          />
        );
      }
      case Widgets.Divider: {
        return <DividerWidget className={classes.gridContainer} key={index} />;
      }
      case Widgets.CallToAction: {
        return <CallToActionWidget key={index} widget={widgetType} isTitle={isTitleWidget} />;
      }
      case Widgets.Marketing: {
        return <MarketingWidget key={index} widget={widgetType} pageIndex={index} />;
      }
      case Widgets.MarketingFeatures: {
        const config = widgetType.content?.namedConfiguration?.[
          '.tocktix.proto.consumer.MarketingFeaturesConfig.namedConfigurationExtension'
        ] as consumer.MarketingFeaturesConfig;

        return (
          <MarketingFeaturesWidget
            key={index}
            tile={config.tile}
            name={widgetType.content?.namedConfiguration?.name || undefined}
            pageIndex={index}
          />
        );
      }
      case Widgets.ModalText: {
        return <ModalTextWidget key={index} widget={widgetType} />;
      }
      case Widgets.CollectionPage: {
        return <CollectionPageWidget />;
      }
      case Widgets.IncludedContent: {
        return <IncludedContentWidget key={index} widget={widgetType} />;
      }
      default: {
        return assertNever(widgetType);
      }
    }
  };

  const isWineShop = widgetSpecificParams?.isWineShop;

  const consumerPageSelected = consumerPage[pathname];
  if (!consumerPageSelected) {
    return (
      <div data-testid="Consumer">
        <LoadingComponent
          isLoading={requestInProgress}
          isBallPulse
          position={isWineShop ? 'INLINE-FULL' : undefined}
        />
      </div>
    );
  }

  const consumerPageVariant = consumerPageSelected.variant[0];
  const consumerPageTemplate = oc(consumerPageVariant).template() as consumer.ConsumerPageTemplate;

  const widgetTypes = getWidgetTypes(consumerPageTemplate.widget);

  const orderedWidgets = _.sortBy(
    _.filter(
      widgetTypes,
      (widget) => (inMobileNative && !oc(widget).widgetInfo.hideInApp()) || !inMobileNative
    ),
    (widget) => oc(widget).widgetInfo.index()
  );

  const attributes = getDocumentAttributesFromTemplate(consumerPage, pathname);
  if (!attributes.image) {
    attributes.image = getFirstPageImage(orderedWidgets);
  }

  return (
    <>
      {attributes.jsonMicrodata ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: attributes.jsonMicrodata }}
        />
      ) : null}
      <Head
        title={attributes.title}
        description={attributes.description}
        keywords={attributes.keywords}
        image={attributes.image}
      />
      {orderedWidgets.map(renderWidget)}
    </>
  );
};
